'use client';

import { PrivyProvider } from '@privy-io/react-auth';
import { toSolanaWalletConnectors } from '@privy-io/react-auth/solana';
import { createSolanaRpc, createSolanaRpcSubscriptions } from '@solana/kit';
import { EnvironmentConstants } from '@drift/common';
import { DriftEnv } from '@drift-labs/sdk';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import { useSolanaLedgerPlugin } from '@privy-io/react-auth/solana';

const findRpcEndpoint = (rpcEndpoint: string) => {
	return EnvironmentConstants.rpcs[
		Env.sdkEnv === 'mainnet-beta' ? 'mainnet' : 'dev'
	].find((rpc) => rpc.value === rpcEndpoint);
};

const getDefaultRpcEndpoint = (env: DriftEnv) => {
	const rpcs =
		EnvironmentConstants.rpcs[env === 'mainnet-beta' ? 'mainnet' : 'dev'];
	return rpcs.find((rpc) => rpc.value.includes('helius')) ?? rpcs[0]; // prioritize helius
};

function SolanaLedgerSetup() {
	// This hook MUST be called inside a component wrapped by PrivyProvider
	useSolanaLedgerPlugin();
	return null;
}

export default function PrivySetupProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [settings] = useCurrentSettings();

	const devnetDefaultRpc = getDefaultRpcEndpoint('devnet');
	const mainnetDefaultRpc = getDefaultRpcEndpoint('mainnet-beta');

	return (
		<PrivyProvider
			key="PrivyProvider"
			appId={process.env.NEXT_PUBLIC_PRIVY_APP_ID ?? ''}
			clientId={process.env.NEXT_PUBLIC_PRIVY_CLIENT_ID ?? ''}
			config={{
				appearance: {
					theme: 'dark',
					showWalletLoginFirst: true,
					walletChainType: 'solana-only',
					walletList: ['phantom', 'solflare', 'detected_solana_wallets'],
				},
				embeddedWallets: {
					solana: {
						createOnLogin: 'off',
					},
					ethereum: {
						createOnLogin: 'off',
					},
				},
				solana: {
					rpcs: {
						'solana:mainnet': {
							rpc: createSolanaRpc(
								settings.rpcEndpoint?.value ?? mainnetDefaultRpc.value
							),
							rpcSubscriptions: createSolanaRpcSubscriptions(
								findRpcEndpoint(
									settings.rpcEndpoint?.value ?? mainnetDefaultRpc.value
								)?.wsValue ??
									mainnetDefaultRpc.wsValue ??
									''
							),
						},
						'solana:devnet': {
							rpc: createSolanaRpc(
								settings.rpcEndpoint?.value ?? devnetDefaultRpc.value
							),
							rpcSubscriptions: createSolanaRpcSubscriptions(
								findRpcEndpoint(
									settings.rpcEndpoint?.value ?? devnetDefaultRpc.value
								)?.wsValue ??
									devnetDefaultRpc.wsValue ??
									''
							),
						},
					},
				},
				externalWallets: {
					solana: {
						connectors: toSolanaWalletConnectors(), // For detecting EOA browser wallets
					},
				},
			}}
		>
			<SolanaLedgerSetup />
			{children}
		</PrivyProvider>
	);
}
